/**
 * Мост к приложениям для Mac и iPhone (`apps/apple`). Приложение показывает этот же интерфейс в WKWebView,
 * а уведомления, число на значке, вход через Google и команды меню делает само.
 *
 * Приложение до загрузки страницы кладёт в `window.chatNative` платформу, версию и разрешение на уведомления,
 * сообщения от страницы принимает обработчик `webkit.messageHandlers.chat`, а своё (щелчок по уведомлению, ответ
 * на запрос разрешения, команда меню) присылает событием `chat-native` на `window`.
 */


export type NativePermission = 'granted' | 'denied' | 'default';

export type NativeEvent =
  | { readonly type: 'notificationClick'; readonly id: string }
  | { readonly type: 'notificationsPermission'; readonly value: NativePermission }
  | { readonly type: 'command'; readonly name: string }
  /** Сеть пропала или вернулась (NWPathMonitor в приложении); начальное значение — `chatNative.online`. */
  | { readonly type: 'network'; readonly online: boolean }
  /** Fn нажали / отпустили / сочетание с другой клавишей (отмена): диктовка в приложении для Mac. */
  | { readonly type: 'dictationKey'; readonly op: 'down' | 'up' | 'cancel' | 'finish'; readonly global: boolean }
  /** Звук, который записало приложение для Mac при диктовке в другой программе (base64; пусто — записать не вышло). */
  /** Текст, который приложение для Mac получило встроенным распознаванием macOS (пусто — речи не нашлось). */
  | { readonly type: 'dictationText'; readonly text: string }
  | { readonly type: 'dictationAudio'; readonly base64: string; readonly mime: string }
  /** Диктовке в других программах нужно право в системе (`input` — «Мониторинг ввода», `accessibility` — «Универсальный доступ») или закрыть программу с такой же диктовкой (`rival`, имя в `app`). */
  | { readonly type: 'dictationNeeds'; readonly what: 'input' | 'accessibility' | 'rival' | 'microphone' | 'speech'; readonly app: string }
  /** Приложение для Mac сняло экран (ScreenCaptureKit): картинка PNG в base64 или распознанный текст (режим «text»). */
  /** Какие из глобальных сочетаний снимков системе не удалось занять (они заняты другим приложением). */
  | { readonly type: 'screenshotKeysFailed'; readonly failed: readonly string[] }
  /** Человек выбрал папку для снимков в окне macOS. */
  | { readonly type: 'screenshotFolder'; readonly path: string }
  | { readonly type: 'screenshot'; readonly mode: string; readonly mime: string; readonly base64: string; readonly text: string; readonly target?: string; readonly cancelled?: boolean };

interface NativeInfo {
  readonly platform: 'macos' | 'ios';
  readonly version: string;
  readonly notifications: NativePermission;
}

function readInfo(): NativeInfo | null {
  if (typeof window === 'undefined') return null;
  const info: unknown = Reflect.get(window, 'chatNative');
  if (typeof info !== 'object' || info === null) return null;
  const platform: unknown = Reflect.get(info, 'platform');
  const version: unknown = Reflect.get(info, 'version');
  const notifications: unknown = Reflect.get(info, 'notifications');
  if (platform !== 'macos' && platform !== 'ios') return null;
  return {
    platform,
    version: typeof version === 'string' ? version : '',
    notifications: notifications === 'granted' || notifications === 'denied' ? notifications : 'default',
  };
}

/** Страница открыта в любом приложении Chat (Mac, iPhone, Windows): приложение кладёт в окно `chatNative`. В обычном браузере его нет. */
export function inChatApp(): boolean {
  const info: unknown = Reflect.get(window, 'chatNative');
  return typeof info === 'object' && info !== null;
}

/** Страница открыта в приложении для Mac или iPhone. */
export function nativeApp(): NativeInfo | null {
  return readInfo();
}

/**
 * Приложение умеет открывать папку синхронизации («Chat by Passwork») в Finder: ставит `chatNative.syncFolder = true`
 * и принимает `{ type: 'syncFolder', op: 'reveal' }`. В браузере и в старых сборках приложения кнопки нет.
 */
export function canRevealSyncFolder(): boolean {
  const info: unknown = typeof window === 'undefined' ? null : Reflect.get(window, 'chatNative');
  return readInfo()?.platform === 'macos' && typeof info === 'object' && info !== null && Reflect.get(info, 'syncFolder') === true;
}

/** Открыть папку синхронизации в Finder (в приложении для Mac). */
export function revealSyncFolder(): boolean {
  return postNative({ type: 'syncFolder', op: 'reveal' });
}

/** Отправить сообщение приложению. Без приложения — false, и вызывающий делает всё сам (как в браузере). */
export function postNative(message: Readonly<Record<string, unknown>>): boolean {
  if (readInfo() === null) return false;
  const webkit: unknown = Reflect.get(window, 'webkit');
  const handlers: unknown = typeof webkit === 'object' && webkit !== null ? Reflect.get(webkit, 'messageHandlers') : null;
  const chat: unknown = typeof handlers === 'object' && handlers !== null ? Reflect.get(handlers, 'chat') : null;
  const post: unknown = typeof chat === 'object' && chat !== null ? Reflect.get(chat, 'postMessage') : null;
  if (typeof post !== 'function') return false;
  Reflect.apply(post, chat, [message]);
  return true;
}

/** Подписка на события приложения; возвращает отписку. */
export function onNative(listener: (event: NativeEvent) => void): () => void {
  const handle = (e: Event) => {
    if (!(e instanceof CustomEvent)) return;
    const detail: unknown = e.detail;
    if (typeof detail !== 'object' || detail === null) return;
    const type: unknown = Reflect.get(detail, 'type');
    if (type === 'notificationClick') {
      const id: unknown = Reflect.get(detail, 'id');
      if (typeof id === 'string') listener({ type, id });
    } else if (type === 'notificationsPermission') {
      const value: unknown = Reflect.get(detail, 'value');
      listener({ type, value: value === 'granted' || value === 'denied' ? value : 'default' });
    } else if (type === 'command') {
      const name: unknown = Reflect.get(detail, 'name');
      if (typeof name === 'string') listener({ type, name });
    } else if (type === 'network') {
      const online: unknown = Reflect.get(detail, 'online');
      if (typeof online === 'boolean') listener({ type, online });
    } else if (type === 'dictationKey') {
      const op: unknown = Reflect.get(detail, 'op');
      if (op === 'down' || op === 'up' || op === 'cancel' || op === 'finish') listener({ type, op, global: Reflect.get(detail, 'global') === true });
    } else if (type === 'dictationText') {
      const text: unknown = Reflect.get(detail, 'text');
      listener({ type, text: typeof text === 'string' ? text : '' });
    } else if (type === 'dictationAudio') {
      const base64: unknown = Reflect.get(detail, 'base64');
      const mime: unknown = Reflect.get(detail, 'mime');
      listener({ type, base64: typeof base64 === 'string' ? base64 : '', mime: typeof mime === 'string' ? mime : 'audio/mp4' });
    } else if (type === 'dictationNeeds') {
      const what: unknown = Reflect.get(detail, 'what');
      const app: unknown = Reflect.get(detail, 'app');
      if (what === 'input' || what === 'accessibility' || what === 'rival' || what === 'microphone' || what === 'speech') listener({ type, what, app: typeof app === 'string' ? app : '' });
    } else if (type === 'screenshot' && Reflect.get(detail, 'op') === 'folder') {
      const path: unknown = Reflect.get(detail, 'path');
      if (typeof path === 'string' && path !== '') listener({ type: 'screenshotFolder', path });
    } else if (type === 'screenshot' && Reflect.get(detail, 'op') === 'hotkeysResult') {
      const failed: unknown = Reflect.get(detail, 'failed');
      listener({ type: 'screenshotKeysFailed', failed: Array.isArray(failed) ? failed.filter((m): m is string => typeof m === 'string') : [] });
    } else if (type === 'screenshot') {
      const mode: unknown = Reflect.get(detail, 'mode');
      const mime: unknown = Reflect.get(detail, 'mime');
      const base64: unknown = Reflect.get(detail, 'base64');
      const text: unknown = Reflect.get(detail, 'text');
      const target: unknown = Reflect.get(detail, 'target');
      const cancelled = Reflect.get(detail, 'cancelled') === true;
      if (typeof mode === 'string') {
        listener({ type, mode, mime: typeof mime === 'string' ? mime : 'image/png', base64: typeof base64 === 'string' ? base64 : '', text: typeof text === 'string' ? text : '', ...(typeof target === 'string' ? { target } : {}), ...(cancelled ? { cancelled } : {}) });
      }
    }
  };
  window.addEventListener('chat-native', handle);
  return () => {
    window.removeEventListener('chat-native', handle);
  };
}

/** Приложение сообщает состояние сети (`chatNative.online`); без приложения — null, и страница смотрит на `navigator.onLine`. */
export function nativeOnline(): boolean | null {
  const info: unknown = typeof window === 'undefined' ? null : Reflect.get(window, 'chatNative');
  const value: unknown = typeof info === 'object' && info !== null ? Reflect.get(info, 'online') : null;
  return typeof value === 'boolean' ? value : null;
}

/** Тактильный отклик на iPhone: `chatNative.haptic = true`, сообщение `{ type: 'haptic', style }`. Без приложения — ничего. */
export function nativeHaptic(style: 'light' | 'medium' = 'light'): void {
  const info: unknown = typeof window === 'undefined' ? null : Reflect.get(window, 'chatNative');
  if (typeof info === 'object' && info !== null && Reflect.get(info, 'haptic') === true) postNative({ type: 'haptic', style });
}

/** Страница открыта в приложении для Mac: тексты про показ экрана говорят «Chat спросит», а не «браузер». */
export function inMacApp(): boolean {
  return readInfo()?.platform === 'macos';
}
